import { Button, formUtils } from '@insurancemenu/imenu-components-lib';
import React from 'react';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import { CustomFormEntities, FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import clsx from 'clsx';
import { createStyles } from './styles';
import {
  DYNAMIC_FORM_JSON_BACK_BTN_TEST_ID,
  DYNAMIC_FORM_JSON_SAVE_BTN_TEST_ID,
  DYNAMIC_FORM_JSON_SPINNER_TEST_ID,
} from '../constants';

interface Props {
  'data-test-id'?: string;
  /**
   * Instance of DynamicFormController for form management.
   */
  dynamicForm: formUtils.DynamicFormController | null;

  /**
   * Form object containing sections to be rendered.
   */
  form: FormObjectFromJSON;

  /**
   * Indicates if the form is currently loading.
   * Displays a spinner and disables the save button (if using the common footer).
   */
  loading: boolean;

  /**
   * Text for the back button.
   * Defaults to 'Exit' if not provided.
   */
  backBtnText?: string;

  /**
   * Text for the complete button.
   * Defaults to 'Complete' if not provided.
   */
  completeBtnText?: string;

  /**
   * Indicates if the form is completed.
   * Affects the behavior of the back button.
   */
  isFormCompleted?: boolean;

  /**
   * Custom form components for use in the formUtils renderEntity method.
   */
  customComponents?: CustomFormEntities;

  /**
   * Option to hide the common form footer.
   * Useful if a custom footer is provided or if the form is for display only.
   */
  hideCommonFormFooter?: boolean;

  /**
   * Indicates if the form is editable.
   * If false, a semi-transparent backdrop blocks form updates.
   */
  isEditable?: boolean;

  /**
   * Optional class name for the form body.
   */
  formBodyClassName?: string;

  /**
   * Optional custom footer component that uses the form’s validity status.
   * Helps control button availability in the parent component.
   */
  customFormFooter?: (isFormValid: boolean) => JSX.Element;

  /**
   * Callback to complete the form.
   * The complete button will not be rendered if this is not provided.
   */
  onCompleteForm?: () => Promise<void> | void;

  /**
   * Callback to save and exit the form.
   * Defines the behavior of the back button.
   */
  onSaveAndExit?: () => Promise<void> | void;

  /**
   * Callback to exit without saving.
   * Intended for non-editable forms.
   */
  onBack?: () => void;
}

/**
 * Renders a dynamic form from JSON (sections, entities, etc.).
 *
 * @deprecated Use `DynamicJSONForm` with `formFooterProps` and no warning modal.
 * This version has a hardcoded warning modal and is kept temporarily to avoid breaking changes — should be removed later.
 */
const DynamicFormFromJSON = observer((props: Props): JSX.Element => {
  const {
    dynamicForm,
    form,
    loading,
    backBtnText,
    completeBtnText,
    isFormCompleted,
    customComponents,
    hideCommonFormFooter = false,
    isEditable = true,
    formBodyClassName = '',
    customFormFooter,
    onCompleteForm,
    onSaveAndExit,
    onBack,
  } = props;

  const {
    exitModalStore: { openModal },
  } = useRootStore();

  const classes = createStyles();
  // TODO:
  // OBSOLETE This logic needs to be removed. The parent component should handle it.
  // Exit modal will be removed from root store as well
  // Alternatively, create an additional wrapper component for this purpose.
  const onExit = isFormCompleted ? openModal : onSaveAndExit;

  const onFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (onCompleteForm) {
      void onCompleteForm();
    }
  };

  return (
    <form onSubmit={onFormSubmit} data-test-id={props['data-test-id']}>
      <div className={clsx(classes.formBody, formBodyClassName, !isEditable && classes.disableFormBody)}>
        <div className={classes.innerWrapper}>
          {dynamicForm && formUtils.renderSections(form, dynamicForm, customComponents)}
          <FormsSpinner display={loading} styles={classes.spinnerBg} data-test-id={DYNAMIC_FORM_JSON_SPINNER_TEST_ID} />
        </div>
      </div>
      {!hideCommonFormFooter && (
        <footer className={classes.footer}>
          {(onBack || onExit) && (
            <Button
              type="outlined"
              size="large"
              width="24rem"
              onClick={onBack || onExit}
              className={classes.button}
              data-test-id={DYNAMIC_FORM_JSON_BACK_BTN_TEST_ID}
            >
              {backBtnText || 'Exit'}
            </Button>
          )}
          {onCompleteForm && (
            <Button
              size="large"
              width="24rem"
              color="primary"
              htmlType="submit"
              disabled={!dynamicForm?.areAllFieldsValid || loading}
              className={classes.button}
              data-test-id={DYNAMIC_FORM_JSON_SAVE_BTN_TEST_ID}
            >
              {completeBtnText || 'Complete'}
            </Button>
          )}
        </footer>
      )}
      {customFormFooter && customFormFooter(dynamicForm?.areAllFieldsValid || false)}
    </form>
  );
});

export default DynamicFormFromJSON;
